/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, {
  CSSProperties,
  FC,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import cx from 'classnames';
import { forEach, lowerCase } from 'lodash';

import IconButton from '@broadleaf/admin-components/dist/common/components/IconButton';
import useFormatMessage from '@broadleaf/admin-components/dist/common/hooks/useFormatMessage';
import { fragment } from '@broadleaf/admin-components/dist/common/utils/path';
import AuthService from '@broadleaf/admin-components/dist/authentication/services/AuthService';

import messages from './AssetPreview.messages';
import useSecureAssetUrl from '../../../../hooks/useSecureAssetUrl';

/**
 * Component that renders a preview of the `type` of asset at the provided `src`.
 *
 * ```js
 * <AssetPreview type="image" src="/cat.png"/>
 * ```
 */
export const AssetPreview: FC<AssetPreviewProps> = ({
  alt,
  className,
  style,
  type,
  openInNewTab = true,
  ...rest
}) => {
  const AssetComponent = getAssetComponent(type);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current && style) {
      forEach<CSSProperties>(style, (value, key) => {
        ref.current.style[key] = value;
      });
    }
  }, [style]);

  return (
    <div
      aria-label={alt}
      ref={ref}
      className={cx(
        'AssetPreview checkered tw-group tw-relative tw-flex tw-justify-around tw-rounded tw-text-gray-500',
        className
      )}
    >
      {openInNewTab && type !== 'embed' && <OpenInNewTab {...rest} />}
      <AssetComponent
        alt={alt}
        embedCode={rest.embedCode}
        src={rest.src}
        type={type}
        style={rest.assetStyle}
      />
    </div>
  );
};

export interface AssetPreviewProps {
  /**
   * The alt text for the asset being previewed
   */
  alt?: string;
  /**
   * The optional className for the top-level element.
   */
  className?: string;
  /**
   * For "embedded" type asset previews this is the embed code.
   */
  embedCode?: string;
  /**
   * Whether or not to render the OpenInNewTab action.
   */
  openInNewTab?: boolean;
  /**
   * The option styles for the top-level element.
   */
  style?: CSSProperties;
  /**
   * The src of the asset that is being previewed
   */
  src?: string;
  /**
   * The type of asset that is being previewed, e.g. "image", "embedded", etc.
   */
  type?: string;
  /**
   * The option styles for the asset component.
   */
  assetStyle?: CSSProperties;
}

/**
 * Helper function that returns the component to render for the Asset based on its type.
 *
 * @param {String} type the type of the Asset, e.g. "image" or "embed"
 */
const getAssetComponent = (
  type: string
): FunctionComponent<AssetComponentProps> => {
  switch (lowerCase(type)) {
    case 'image':
      return ImageAssetPreview;
    case 'embed':
      return EmbedAssetPreview;
    default:
      // all other asset types render an AssetPlaceholder
      return AssetPlaceholder;
  }
};

/**
 * Helper component used to the render the `img` tag for an image preview.
 */
const ImageAssetPreview: FC<AssetComponentProps> = props => {
  const [hasError, setHasError] = useState(false);
  const ref = useRef<HTMLImageElement>(null);
  const { style } = props;
  //RM - Use secure asset url hook to get the full URL with token if needed
  const { fullAssetUrl } = useSecureAssetUrl(props.src);

  useEffect(() => {
    if (ref.current && style) {
      forEach<React.CSSProperties>(style, (value, key) => {
        ref.current.style[key] = value;
      });
    }
  }, [style]);

  useEffect(() => {
    // clear the error if the src url changes
    setHasError(false);
  }, [fullAssetUrl]);

  if (hasError || !fullAssetUrl) {
    return <AssetPlaceholder {...props} src={fullAssetUrl} />;
  }

  return (
    <img
      ref={ref}
      alt={props.alt}
      className="tw-object-contain"
      onError={() => setHasError(true)}
      src={fullAssetUrl}
    />
  );
};

/**
 * Helper component used to render the embed preview.
 */
const EmbedAssetPreview: React.FC<AssetComponentProps> = props => {
  const ref = useRef<HTMLDivElement>(null);
  const { style } = props;

  useEffect(() => {
    if (ref.current && style) {
      forEach<React.CSSProperties>(style, (value, key) => {
        ref.current.style[key] = value;
      });
    }
  }, [style]);

  return (
    <div
      ref={ref}
      className="tw-mx-auto tw-max-h-full tw-max-w-full"
      dangerouslySetInnerHTML={{
        __html: props.embedCode
      }}
      title={props.alt}
    />
  );
};

/**
 * Helper component used to render a placeholder for assets without a proper preview.
 */
const AssetPlaceholder: FC<AssetComponentProps> = props => {
  const iconClassName = getIconClassName(props.type);
  const fileName = props.src ? fragment(props.src, -1, false) : '';
  const ref = useRef<HTMLDivElement>(null);
  const { style } = props;

  useEffect(() => {
    if (ref.current && style) {
      forEach<React.CSSProperties>(style, (value, key) => {
        ref.current.style[key] = value;
      });
    }
  }, [style]);

  return (
    <div
      ref={ref}
      className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-pt-[100%] tw-text-base tw-font-bold"
      title={fileName}
    >
      <i aria-label={props.alt} className={`${iconClassName} tw-mt-[-100%]`} />
    </div>
  );
};

interface AssetComponentProps {
  /**
   * The alt text for the asset being previewed
   */
  alt: string;
  /**
   * The type of asset that is being previewed, e.g. "image", "embedded", etc.
   */
  type: string;
  /**
   * For "embedded" type asset previews this is the embed code.
   */
  embedCode?: string;
  /**
   * The src of the asset that is being previewed
   */
  src: string;
  /**
   * The option styles for the top-level element.
   */
  style?: CSSProperties;
}

function getIconClassName(type: string) {
  type = lowerCase(type);

  switch (type) {
    case 'image':
    case 'audio':
    case 'video':
    case 'csv':
    case 'excel':
    case 'pdf':
    case 'word':
      return `fa fa-file-${type}`;
    case 'text':
      return 'fa fa-file-alt';
    default:
      return 'fa fa-file';
  }
}

const OpenInNewTab: FC<OpenInNewTabProps> = props => {
  const formatMessage = useFormatMessage();
  //RM - Use secure asset url hook to get the full URL with token if needed
  const { fullAssetUrl } = useSecureAssetUrl(props.src);
  return (
    <a
      className="tw-absolute tw-left-2 tw-top-2 tw-z-10 tw-hidden tw-text-base group-hover:tw-block"
      href={fullAssetUrl}
      onClick={e => {
        e.stopPropagation();
      }}
      rel="noopener noreferrer"
      target="_blank"
      title={formatMessage(messages.openInNewTab)}
    >
      <IconButton size="sm" color="shaded" name="binoculars" />
    </a>
  );
};

interface OpenInNewTabProps {
  /**
   * The src of the asset that is being previewed
   */
  src?: string;
}

export default AssetPreview;
